import React from 'react';
import { HEADER_CELL } from '../../utils/tableColumns';
import { ivrOutcome } from '../../utils/ivrOutcome';
import {
  IVR_STATUS_CONFIG as statusConfig,
  formatShortDate,
  submitterName,
} from './ivrPresentation';

// Presentation only. The owning page supplies scoped records and retains every action.
export default function IVRListTable({
  records,
  admin = false,
  distributors = [],
  showDistributor = false,
  t,
  locale,
  onView,
  onDelete,
  formatDate,
  dateLabel = 'DATE SUBMITTED',
  label = 'IVR requests',
  renderStatus,
}) {
  // ONE grid for every role. Reps used to get a narrower table without CLINIC, SUBMITTED BY
  // or DATE, and a rep covering fourteen practices could not tell whose IVR was whose. The server
  // already scopes every row it returns; which columns show is presentation, and every role needs
  // these. Flexible tracks carry a px minimum and the badge/date/button columns are fixed px, so
  // the header grid and the row grid resolve to identical widths - see utils/tableColumns.
  const ivrGrid = showDistributor
    ? 'grid-cols-[minmax(104px,2.43fr)_minmax(88px,2.26fr)_minmax(116px,3.12fr)_minmax(120px,3fr)_minmax(116px,2.7fr)_minmax(100px,2.39fr)_minmax(156px,2.39fr)_112px]'
    : 'grid-cols-[minmax(104px,2.43fr)_minmax(88px,2.26fr)_minmax(116px,3.12fr)_minmax(116px,2.7fr)_minmax(100px,2.39fr)_minmax(156px,2.39fr)_112px]';
  const ivrColumns = [
    'PATIENT NAME',
    'MEDICARE ID',
    'CLINIC',
    ...(showDistributor ? ['Distributor'] : []),
    'SUBMITTED BY',
    dateLabel,
    'STATUS',
    'ACTIONS',
  ];
  return (
    <div
      role="table"
      aria-label={t(label)}
      className={showDistributor ? 'min-w-[960px]' : undefined}
    >
      <div
        role="rowgroup"
        className="bg-[rgba(226,232,240,0.2)] border-b border-[#e2e8f0] rounded-t-[14px]"
      >
        <div role="row" className={`grid ${ivrGrid} px-5 py-4`}>
          {ivrColumns.map((col) => (
            <span role="columnheader" key={col} className={HEADER_CELL}>
              {t(col)}
            </span>
          ))}
        </div>
      </div>
      <div role="rowgroup" className="divide-y divide-[#e2e8f0]">
        {records.length === 0 ? (
          <div role="row" className="px-5 py-8 text-center text-sm text-[#64748b]">
            <span role="cell">{t('No IVR requests found')}</span>
          </div>
        ) : (
          records.map((record) => {
            const sts = statusConfig[ivrOutcome(record)] || statusConfig.submitted;
            const name =
              `${record.patient?.firstName || ''} ${record.patient?.lastName || ''}`.trim();
            const organization = distributors.find(
              (row) => row._id === (record.distributorId?._id || record.distributorId)
            );
            return (
              <div
                key={record._id}
                role="row"
                className={`grid ${ivrGrid} px-5 py-4 items-center hover:bg-gray-50/50 transition-colors`}
              >
                <span
                  role="cell"
                  className="text-xs font-medium text-[#0f172a] truncate"
                  title={name}
                >
                  {name}
                </span>
                <span role="cell" className="text-xs font-medium text-[#0f172a] truncate">
                  {record.insurance?.medicareId || t('N/A')}
                </span>
                {
                  <>
                    <span
                      role="cell"
                      className="text-xs font-medium text-[#0f172a] truncate"
                      title={record.clinic?.name || ''}
                    >
                      {record.clinic?.name || '\u2014'}
                      {admin && !showDistributor && (
                        <span className="block text-[10px] text-[#64748b] truncate">
                          {record.businessContext === 'distributor'
                            ? `${t('Distributor')}${organization ? ` · ${organization.name}` : ''}`
                            : t('Direct MedEffects')}
                        </span>
                      )}
                    </span>
                    {showDistributor && (
                      <span
                        role="cell"
                        className="text-xs font-medium text-[#0f172a] truncate"
                        title={organization?.name || record.distributorId?.name || t('Unavailable')}
                      >
                        {organization?.name || record.distributorId?.name || t('Unavailable')}
                      </span>
                    )}
                    {/*
                          The heading says SUBMITTED BY rather than "Sales Rep" because a clinic
                          login and an admin can submit too, and filing either under a rep heading
                          would be wrong on the one screen used to work out who did what. When the
                          submitter is NOT a rep the role is shown beside the name, so a rep row
                          reads as a plain name - which is the case this was asked for.
                        */}
                    <span
                      role="cell"
                      className="text-xs font-medium text-[#0f172a] truncate"
                      title={submitterName(record, t)}
                    >
                      {submitterName(record, t)}
                    </span>
                    <span role="cell" className="text-xs font-medium text-[#64748b] truncate">
                      {(formatDate
                        ? formatDate(record.createdAt)
                        : formatShortDate(record.createdAt, locale)) || '\u2014'}
                    </span>
                  </>
                }
                {renderStatus ? (
                  <span role="cell">{renderStatus(record)}</span>
                ) : (
                  <span
                    role="cell"
                    className={`inline-flex items-center justify-center w-fit px-2.5 py-0.5 rounded-[6px] text-xs font-medium ${sts.bg} ${sts.text}`}
                  >
                    {t(sts.label)}
                  </span>
                )}
                <span role="cell" className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => onView(record)}
                    aria-label={t('View IVR details')}
                    className="inline-flex items-center gap-1.5 w-fit px-2.5 py-1 rounded-[6px] border border-[#e2e8f0] text-xs font-medium text-[#0f172a] hover:bg-gray-50 hover:border-[#cbd5e1] transition-colors"
                  >
                    <span className="material-symbols-outlined text-[16px] text-[#64748b]">
                      visibility
                    </span>
                    {t('View')}
                  </button>
                  {admin && (
                    <button
                      type="button"
                      onClick={() => onDelete(record)}
                      aria-label={t('Delete IVR {id}', { id: record.requestId || '' })}
                      title={t('Delete IVR request')}
                      className="inline-flex items-center justify-center w-7 h-7 rounded-[6px] border border-[#e2e8f0] text-[#64748b] hover:bg-[#fef2f2] hover:text-[#b91c1c] hover:border-[#fecaca] transition-colors"
                    >
                      <span className="material-symbols-outlined text-[16px]">delete</span>
                    </button>
                  )}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
